import {LastPeriodicCheck, Machine, PeriodicCheckImage, PeriodicCheckImageMeta, PeriodicCheckPayload, PeriodicChecksPayload} from '~/types';
import { PERIODIC_CHECK_DRAFT_STATUS } from '~/app/data/statuses/periodicCheck';
import { InspectionTypes } from '~/periodicCheck/store';
import {
    mergeSavedChecksIntoTemplates,
    PeriodicCheckSessionMode,
    savedCheckToUiPayload,
    usableCheckId,
} from '~/periodicCheck/offlinePeriodicCheck';

export type PeriodicCheckSaveItem = Omit<PeriodicCheckPayload, 'images' | 'im'> & {
    existingImages: string[]
    existingIm: PeriodicCheckImageMeta[]
    newImages: File[]
    newIm: PeriodicCheckImageMeta[]
}

export const buildChecksForSave = (checks: PeriodicCheckPayload[]): PeriodicCheckSaveItem[] => checks.map(buildCheckItemForSave);

export const resolvePeriodicCheckDraft = (
    source: Pick<Machine, 'checks' | 'acceptanceInspectionChecks' | 'checkId' | 'checkData'>,
    lastRegisteredCheck?: LastPeriodicCheck,
    inspectionType?: number,
    queued?: PeriodicChecksPayload | null,
): { draftChecks: PeriodicCheckPayload[] | undefined, dailyCheckId: string | null, sessionMode: PeriodicCheckSessionMode } => {
    const isAcceptance = inspectionType === InspectionTypes.ACCEPTANCE_INSPECTION;
    const templates = isAcceptance ? source.acceptanceInspectionChecks : source.checks;

    if (queued?.checks?.length) {
        const saved = queued.checks.map(savedCheckToUiPayload);
        return {
            draftChecks: templates?.length ? mergeSavedChecksIntoTemplates(templates, saved) : saved,
            dailyCheckId: usableCheckId(queued.id),
            sessionMode: queued.isDraft ? 'editable_draft' : 'submitted_pending',
        };
    }

    const isDraft = lastRegisteredCheck?.status === PERIODIC_CHECK_DRAFT_STATUS;
    const pendingSync = !!lastRegisteredCheck?.pendingSync;
    let draftChecks: PeriodicCheckPayload[] | undefined = templates?.length
        ? templates as PeriodicCheckPayload[]
        : undefined;
    if ((isDraft || pendingSync) && source.checkData?.checks?.length) {
        draftChecks = templates?.length
            ? mergeSavedChecksIntoTemplates(templates, source.checkData.checks)
            : source.checkData.checks.map(savedCheckToUiPayload);
    }

    const dailyCheckId = (isDraft || pendingSync)
        ? (usableCheckId(lastRegisteredCheck?.id) ?? usableCheckId(source.checkData?.id))
        : usableCheckId(source.checkId);

    return {
        draftChecks,
        dailyCheckId,
        sessionMode: pendingSync ? 'submitted_pending' : isDraft ? 'editable_draft' : 'editable',
    };
};

const isFile = (value: any): value is File | Blob =>
    value instanceof globalThis.File || value instanceof globalThis.Blob;

type ClassifiedImage =
  | { kind: 'string'; value: string }
  | { kind: 'file'; value: File }
  | { kind: 'record'; value: PeriodicCheckImage };

const classifyImage = (image: PeriodicCheckImageInput): ClassifiedImage | null => {
    if (typeof image === 'string') return { kind: 'string', value: image };
    if (isFile(image)) return { kind: 'file', value: image as File };
    if (isPeriodicCheckImageRecord(image)) return { kind: 'record', value: image };
    return null;
};

export const buildCheckItemForSave = (check: PeriodicCheckPayload): PeriodicCheckSaveItem => {
    const { images, im, existingImages, existingIm, newImages, newIm, ...rest } = check;
    if (!(images?.length)) {
        return {
            ...rest,
            existingImages: existingImages ?? [],
            existingIm: existingIm ?? [],
            newImages: newImages ?? [],
            newIm: newIm ?? [],
        };
    }
    const splitExistingImages: string[] = [];
    const splitExistingIm: PeriodicCheckImageMeta[] = [];
    const splitNewImages: File[] = [];
    const splitNewIm: PeriodicCheckImageMeta[] = [];

    images.forEach((image, index) => {
        const meta = im?.[index] ?? {};
        const classified = classifyImage(image);
        if (!classified) return;
    
        if (classified.kind === 'file') {
            splitNewImages.push(classified.value);
            splitNewIm.push(meta);
            return;
        }
    
        const url = classified.kind === 'string' ? classified.value : classified.value.url;
        if (url) {
            splitExistingImages.push(url);
            splitExistingIm.push(meta);
        }
    });

    return {
        ...rest,
        existingImages: splitExistingImages,
        existingIm: splitExistingIm,
        newImages: splitNewImages,
        newIm: splitNewIm,
    };
};

export type PeriodicCheckImageInput =
    | string
    | File
    | PeriodicCheckImage
    | { url?: string; id?: string | number; order?: number; dc?: string; sz?: number };

export const isPeriodicCheckImageRecord = (
    image: PeriodicCheckImageInput,
): image is PeriodicCheckImage =>
    !!image && typeof image === 'object'&& !(image instanceof globalThis.File)
    && 'url' in image && typeof (image as PeriodicCheckImage).url === 'string';

export const getPeriodicCheckImageUrl = (image: PeriodicCheckImageInput): string | null => {
    const classified = classifyImage(image);
    if (!classified) return null;
    switch (classified.kind) {
        case 'string': return classified.value;
        case 'record': return classified.value.url;
        case 'file':   return null;
    }
};

export const normalizePeriodicCheckImages = (
    images?: PeriodicCheckImageInput[],
): (string | File)[] => {
    if (!images?.length) return [];
    const result: (string | File)[] = [];
    for (const image of images) {
        if (typeof image === 'string' || isFile(image)) result.push(image as string | File);
        else if (isPeriodicCheckImageRecord(image)) result.push(image.url);
    }
    return result;
};

export const parsePeriodicCheckImages = (
    images?: PeriodicCheckImageInput[],
    im?: PeriodicCheckImageMeta[],
): { images: (string | File | PeriodicCheckImage)[]; im: PeriodicCheckImageMeta[] } => {
    if (!images?.length) return { images: [], im: [] };
    const parsedImages: (string | File | PeriodicCheckImage)[] = [];
    const parsedIm: PeriodicCheckImageMeta[] = [];
    images.forEach((image, index) => {
        const fallbackMeta = im?.[index] ?? {};
        const classified = classifyImage(image);
        if (!classified) return;
        if (classified.kind === 'string' || classified.kind === 'file') {
            parsedImages.push(classified.value);
            parsedIm.push(fallbackMeta);
            return;
        }
        const rec = classified.value;
        parsedImages.push({
            id: rec.id,
            url: rec.url,
            ...(rec.order != null && { order: rec.order }),
            dc: rec.dc ?? fallbackMeta.dc,
            sz: rec.sz ?? fallbackMeta.sz,
        });
        parsedIm.push({ dc: rec.dc ?? fallbackMeta.dc, sz: rec.sz ?? fallbackMeta.sz });
    });

    return { images: parsedImages, im: parsedIm };
};
